import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuditAction } from '../model/audit.models';
import { AuditService } from '../service/audit.service';
import { ConnectivityService } from '../service/connectivity.service';
import { IndexedDbStorageService } from '../service/indexed-db-storage.service';
import { SecurityEventService } from '../service/security-event.service';
import { MicrosoftAuthConfig, authRedirectUri, authSettings } from './auth.config';
import { SecurityLockService } from './security-lock.service';

const RETURN_URL_KEY = 'tops.auth.returnUrl';
const AUTH_STATE_KEY = 'tops.auth.state';
const AUTH_SESSION_KEY = 'tops.auth.session';
const OFFLINE_AUTH_SESSION_KEY = 'tops.auth.offlineSession';
// Also drives online revalidation (see revalidateSessionWhenOnline).
const OFFLINE_SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const MICROSOFT_REACHABILITY_TIMEOUT_MS = 3000;
const LOGIN_RETRY_DELAY_MS = 2000;
const MAX_LOGIN_RETRY_ATTEMPTS = 8;

interface AuthAccount {
  name?: string;
  username: string;
}

interface AuthSession {
  account: AuthAccount;
  accessToken: string;
  expiresAt: number;
}

interface OfflineAuthSession {
  schemaVersion: 1;
  account: AuthAccount;
  lastLoginAt: number;
  expiresAt: number;
}

interface EncryptedSessionPayload {
  iv: string;
  data: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly connectivity = inject(ConnectivityService);
  private readonly storage = inject(IndexedDbStorageService);
  private readonly securityLock = inject(SecurityLockService);
  private readonly securityEvents = inject(SecurityEventService);
  private readonly audit = inject(AuditService);
  private readonly accountSignal = signal<AuthAccount | null>(null);
  private session: AuthSession | null = null;
  private authenticatedFromOfflineCache = false;
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private authConfigPromise: Promise<MicrosoftAuthConfig> | null = null;
  private redirectingToLogin = false;
  private offlineSessionExpiryTimer: ReturnType<typeof window.setTimeout> | null = null;
  private loginRetryTimer: ReturnType<typeof window.setTimeout> | null = null;
  private loginRetryAttempts = 0;

  constructor() {
    this.connectivity.isOnline$.subscribe((isOnline) => {
      this.authLog('connectivity status changed', { isOnline });
      if (isOnline) void this.revalidateSessionWhenOnline('connectivity-online');
    });

    window.addEventListener('focus', () => void this.revalidateSessionWhenOnline('window-focus'));
    window.addEventListener('pageshow', () => void this.revalidateSessionWhenOnline('page-show'));
    document.addEventListener('visibilitychange', () => {
      this.authLog('visibility changed', { visibilityState: document.visibilityState });
      if (document.visibilityState === 'visible') void this.revalidateSessionWhenOnline('visibility-visible');
    });
  }

  readonly account = this.accountSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.accountSignal());

  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initializationPromise) return this.initializationPromise;

    this.initializationPromise = this.initializeSession();
    return this.initializationPromise;
  }

  private async initializeSession(): Promise<void> {
    this.authLog('initializing session');
    await this.loadStoredSession();

    if (this.isRedirectCallbackRoute()) {
      this.authLog('redirect callback route detected');
      await this.completeRedirectCallback();
      return;
    }

    await this.revalidateSessionWhenOnline('initialize');

    this.initialized = true;
    this.authLog('session initialization complete', { isAuthenticated: this.isAuthenticated() });
  }

  private isRedirectCallbackRoute(): boolean {
    return window.location.pathname.toLowerCase() === authSettings.redirectPath.toLowerCase();
  }

  private async loadStoredSession(): Promise<void> {
    await this.storage.ready();
    const now = Date.now();
    const rawSession = this.storage.getItem(AUTH_SESSION_KEY);
    if (!rawSession) {
      this.authLog('no stored online session found');
      await this.loadOfflineSession(now);
      return;
    }

    try {
      const session = JSON.parse(rawSession) as AuthSession;
      if (!session?.accessToken || !this.validAccount(session.account) || now >= session.expiresAt) {
        this.authLog('stored online session is missing, invalid, or expired', {
          hasAccessToken: !!session?.accessToken,
          hasValidAccount: this.validAccount(session?.account),
          expiresInMs: session?.expiresAt ? session.expiresAt - now : null,
        });
        this.storage.removeItem(AUTH_SESSION_KEY);
        await this.loadOfflineSession(now);
        return;
      }
      this.session = session;
      this.authenticatedFromOfflineCache = false;
      this.accountSignal.set(session.account);
      this.authLog('stored online session restored', { expiresInMs: session.expiresAt - now });
    } catch {
      this.authLog('stored online session could not be parsed');
      this.storage.removeItem(AUTH_SESSION_KEY);
      await this.loadOfflineSession(now);
    }
  }

  private async loadOfflineSession(now = Date.now()): Promise<void> {
    if (this.connectivity.currentOnline) {
      this.authLog('skipping offline session restore while online');
      return;
    }

    const offlineSession = await this.readOfflineSession();
    if (!offlineSession) {
      this.authLog('no offline session found to restore');
      return;
    }

    if (!this.validAccount(offlineSession.account) || now >= offlineSession.expiresAt) {
      this.authLog('offline session is invalid or expired during restore', {
        hasValidAccount: this.validAccount(offlineSession.account),
        expiresInMs: offlineSession.expiresAt - now,
      });
      this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
      this.audit.track({
        category: 'AUTHENTICATION',
        action: AuditAction.OFFLINE_SESSION_EXPIRED,
        severity: 'WARNING',
        outcome: 'FAILURE',
        authorizationDecision: 'DENY',
        message: 'Cached offline session was expired or invalid and has been discarded.',
      });
      return;
    }

    this.session = null;
    this.authenticatedFromOfflineCache = true;
    this.accountSignal.set(offlineSession.account);
    this.scheduleOfflineSessionExpiryCheck(offlineSession.expiresAt);
    this.authLog('offline session restored', { expiresInMs: offlineSession.expiresAt - now });
    this.audit.track({
      category: 'AUTHENTICATION',
      action: AuditAction.OFFLINE_SESSION_RESTORED,
      authorizationDecision: 'ALLOW',
      message: 'Signed in from the encrypted offline session cache.',
    });
  }

  private async completeRedirectCallback(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
      this.authLog('redirect callback returned an error');
      await this.failCallback(params.get('error_description') || error);
      return;
    }

    const code = params.get('code');
    if (!code) {
      this.authLog('redirect callback did not include an auth code');
      if (this.session) await this.goToReturnUrl();
      this.initialized = true;
      return;
    }

    const expectedState = sessionStorage.getItem(AUTH_STATE_KEY);
    const actualState = params.get('state');
    if (expectedState && actualState !== expectedState) {
      this.authLog('redirect callback state mismatch');
      this.audit.track({
        category: 'SECURITY',
        action: AuditAction.LOGIN_FAILED,
        severity: 'CRITICAL',
        outcome: 'FAILURE',
        authorizationDecision: 'DENY',
        message: 'OAuth state mismatch on sign-in callback; possible CSRF attempt.',
      });
      await this.failCallback('Microsoft sign-in state did not match. Please try again.');
      return;
    }

    let session: AuthSession;
    try {
      session = await firstValueFrom(this.http.post<AuthSession>(`${environment.backendApiBaseUrl}/auth/microsoft/callback`, {
        code,
        redirectUri: authRedirectUri,
      }));
    } catch (exchangeError) {
      this.authLog('failed to exchange auth code for session');
      this.audit.track({
        category: 'AUTHENTICATION',
        action: AuditAction.LOGIN_FAILED,
        severity: 'WARNING',
        outcome: 'FAILURE',
        authorizationDecision: 'DENY',
        message: this.authErrorMessage(exchangeError),
      });
      await this.failCallback(this.authErrorMessage(exchangeError));
      return;
    }

    this.session = session;
    this.authenticatedFromOfflineCache = false;
    this.accountSignal.set(session.account);
    this.securityLock.clearLock();
    this.securityEvents.flushQueuedEvents();
    this.audit.track({
      category: 'AUTHENTICATION',
      action: AuditAction.LOGIN_SUCCEEDED,
      authorizationDecision: 'ALLOW',
      message: 'Microsoft SSO sign-in completed on device.',
    });
    this.storage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    await this.storeOfflineSession(session.account);
    sessionStorage.removeItem(AUTH_STATE_KEY);
    this.initialized = true;
    this.authLog('redirect callback completed and sessions stored', { onlineExpiresInMs: session.expiresAt - Date.now() });
    await this.goToReturnUrl();
  }

  private async failCallback(message: string): Promise<void> {
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.setItem('tops.auth.error', message);
    this.initialized = true;
    await this.router.navigate(['/login'], { queryParams: { returnUrl: sessionStorage.getItem(RETURN_URL_KEY) || '/' } });
  }

  private authErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = typeof error.error?.message === 'string' ? error.error.message : '';
      return serverMessage || error.message || 'Microsoft sign-in failed.';
    }
    return error instanceof Error ? error.message : 'Microsoft sign-in failed.';
  }

  private async goToReturnUrl(): Promise<void> {
    const returnUrl = sessionStorage.getItem(RETURN_URL_KEY) || '/';
    sessionStorage.removeItem(RETURN_URL_KEY);
    await this.router.navigateByUrl(returnUrl);
  }

  login(returnUrl = '/'): void {
    void this.startLogin(returnUrl);
  }

  private async startLogin(returnUrl = '/'): Promise<void> {
    if (this.redirectingToLogin) {
      this.authLog('login redirect skipped because redirect is already in progress');
      return;
    }
    if (this.loginRetryTimer) {
      this.authLog('login redirect skipped because a retry is already scheduled');
      return;
    }

    this.redirectingToLogin = true;
    let config: MicrosoftAuthConfig;
    try {
      config = await this.microsoftAuthConfig();
    } catch (configError) {
      this.authLog('Microsoft auth config is not reachable yet; scheduling login retry', {
        error: this.authErrorMessage(configError),
      });
      this.redirectingToLogin = false;
      this.scheduleLoginRetry(returnUrl);
      return;
    }

    if (!await this.canReachMicrosoft(config.authority)) {
      this.redirectingToLogin = false;
      this.scheduleLoginRetry(returnUrl);
      return;
    }

    this.clearLoginRetry();
    this.loginRetryAttempts = 0;
    sessionStorage.setItem(RETURN_URL_KEY, returnUrl);
    const state = crypto.randomUUID();
    sessionStorage.setItem(AUTH_STATE_KEY, state);

    const params = new URLSearchParams({
      client_id: config.clientId,
      response_type: 'code',
      redirect_uri: authRedirectUri,
      response_mode: 'query',
      scope: config.scope || authSettings.scopes.join(' '),
      state,
    });

    this.authLog('starting Microsoft SSO redirect', { returnUrl });
    window.location.assign(`${config.authority}/oauth2/v2.0/authorize?${params.toString()}`);
  }

  private async canReachMicrosoft(authority: string): Promise<boolean> {
    if (!navigator.onLine) return false;

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), MICROSOFT_REACHABILITY_TIMEOUT_MS);
    try {
      const authorityBase = authority.replace(/\/$/, '');
      const response = await fetch(`${authorityBase}/v2.0/.well-known/openid-configuration`, {
        cache: 'no-store',
        signal: controller.signal,
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  private scheduleLoginRetry(returnUrl: string): void {
    if (this.loginRetryTimer) return;
    if (this.loginRetryAttempts >= MAX_LOGIN_RETRY_ATTEMPTS) {
      this.authLog('Microsoft SSO is not reachable; login retry limit reached');
      return;
    }

    this.loginRetryAttempts += 1;
    this.authLog('Microsoft SSO is not reachable yet; retrying login shortly', {
      attempt: this.loginRetryAttempts,
      maxAttempts: MAX_LOGIN_RETRY_ATTEMPTS,
    });
    this.loginRetryTimer = window.setTimeout(() => {
      this.loginRetryTimer = null;
      void this.startLogin(returnUrl);
    }, LOGIN_RETRY_DELAY_MS);
  }

  private clearLoginRetry(): void {
    if (!this.loginRetryTimer) return;

    window.clearTimeout(this.loginRetryTimer);
    this.loginRetryTimer = null;
  }

  async logout(): Promise<void> {
    await this.storage.ready();
    this.audit.track({
      category: 'AUTHENTICATION',
      action: AuditAction.LOGOUT,
      authorizationDecision: 'NOT_APPLICABLE',
      message: 'User signed out.',
    });
    // Flush before the redirect tears down the page, or the sign-out event is lost.
    await this.audit.flush();
    this.storage.removeItem(AUTH_SESSION_KEY);
    this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
    this.clearOfflineSessionExpiryCheck();
    this.clearLoginRetry();
    this.loginRetryAttempts = 0;
    sessionStorage.removeItem(AUTH_STATE_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    this.session = null;
    this.authenticatedFromOfflineCache = false;
    this.accountSignal.set(null);
    const config = await this.microsoftAuthConfig();
    const params = new URLSearchParams({ post_logout_redirect_uri: `${window.location.origin}/login` });
    window.location.assign(`${config.authority}/oauth2/v2.0/logout?${params.toString()}`);
  }

  async accessToken(): Promise<string | null> {
    await this.initialize();
    return this.session && Date.now() < this.session.expiresAt ? this.session.accessToken : null;
  }

  async ensureSessionForOperation(returnUrl = this.currentReturnUrl()): Promise<boolean> {
    await this.initialize();
    if (this.securityLock.isLocked()) return false;
    if (this.isRedirectCallbackRoute() || this.redirectingToLogin) return false;

    await this.storage.ready();
    const now = Date.now();
    if (this.session && now < this.session.expiresAt) return true;

    if (this.session) {
      this.storage.removeItem(AUTH_SESSION_KEY);
      this.session = null;
    }

    const offlineSession = await this.readOfflineSession();
    const offlineSessionLive = this.validAccount(offlineSession?.account) && now < offlineSession.expiresAt;
    if (offlineSessionLive) {
      this.authenticatedFromOfflineCache = true;
      this.accountSignal.set(offlineSession.account);
      this.scheduleOfflineSessionExpiryCheck(offlineSession.expiresAt);
      return true;
    }

    if (offlineSession) {
      this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
    }

    this.clearOfflineSessionExpiryCheck();
    this.authenticatedFromOfflineCache = false;
    this.accountSignal.set(null);

    if (this.connectivity.currentOnline) {
      await this.startLogin(returnUrl);
    }

    return false;
  }

  /**
   * Non-initialising token accessor, safe to call from an HTTP interceptor.
   *
   * <p>{@link accessToken} awaits initialize(), which itself issues HTTP calls and can navigate -
   * calling it from an interceptor would re-enter the interceptor and deadlock. The session is
   * already loaded by the app's startup initialize(), so reading it directly is sufficient.
   */
  currentAccessToken(): string | null {
    return this.session && Date.now() < this.session.expiresAt ? this.session.accessToken : null;
  }

  async restoreOfflineSession(): Promise<boolean> {
    await this.storage.ready();
    if (this.securityLock.isLocked()) return false;
    await this.loadOfflineSession();
    return !!this.accountSignal();
  }

  // Runs whenever connectivity comes online. Uses the offline session's expiry as the clock (not the
  // backend access token), so the revalidation window can be tuned/tested via OFFLINE_SESSION_TTL_MS.
  private async revalidateSessionWhenOnline(reason = 'unknown'): Promise<void> {
    if (!this.connectivity.currentOnline || this.isRedirectCallbackRoute() || this.redirectingToLogin) {
      this.authLog('online revalidation skipped', {
        reason,
        isOnline: this.connectivity.currentOnline,
        isRedirectCallbackRoute: this.isRedirectCallbackRoute(),
        redirectingToLogin: this.redirectingToLogin,
      });
      return;
    }

    await this.storage.ready();
    const now = Date.now();
    const offlineSession = await this.readOfflineSession();
    if (!offlineSession) {
      this.clearOfflineSessionExpiryCheck();
      this.authLog('online revalidation found no offline session', {
        reason,
        hasAccount: !!this.accountSignal(),
        hasOnlineSession: !!this.session,
      });
      if (this.accountSignal() || this.session) {
        this.session = null;
        this.authenticatedFromOfflineCache = false;
        this.accountSignal.set(null);
        await this.startLogin(this.currentReturnUrl());
      }
      return;
    }

    const sessionLive = this.validAccount(offlineSession.account) && now < offlineSession.expiresAt;
    if (sessionLive) {
      this.authenticatedFromOfflineCache = true;
      this.accountSignal.set(offlineSession.account);
      this.scheduleOfflineSessionExpiryCheck(offlineSession.expiresAt);
      this.authLog('offline session still live during online revalidation', {
        reason,
        expiresInMs: offlineSession.expiresAt - now,
      });
      return;
    }

    this.authLog('offline session expired during online revalidation; starting SSO', {
      reason,
      expiresInMs: offlineSession.expiresAt - now,
    });
    this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
    this.clearOfflineSessionExpiryCheck();
    this.session = null;
    this.authenticatedFromOfflineCache = false;
    this.accountSignal.set(null);
    await this.startLogin(this.currentReturnUrl()); // expired while offline: SSO must challenge the user
  }

  private currentReturnUrl(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
  }

  private async storeOfflineSession(account: AuthAccount): Promise<void> {
    if (!this.validAccount(account)) return;

    const now = Date.now();
    const offlineSession: OfflineAuthSession = {
      schemaVersion: 1,
      account,
      lastLoginAt: now,
      expiresAt: now + OFFLINE_SESSION_TTL_MS,
    };

    const encryptedSession = await this.encryptOfflineSession(offlineSession);
    if (encryptedSession) {
      this.storage.setItem(OFFLINE_AUTH_SESSION_KEY, encryptedSession);
      this.scheduleOfflineSessionExpiryCheck(offlineSession.expiresAt);
      this.authLog('offline session stored', { expiresInMs: offlineSession.expiresAt - now });
    }
  }

  private scheduleOfflineSessionExpiryCheck(expiresAt: number): void {
    this.clearOfflineSessionExpiryCheck();
    const delay = Math.max(expiresAt - Date.now(), 0);
    this.authLog('scheduled offline session expiry check', { delayMs: delay });
    this.offlineSessionExpiryTimer = window.setTimeout(() => {
      this.offlineSessionExpiryTimer = null;
      void this.revalidateSessionWhenOnline('offline-session-expiry-timer');
    }, delay);
  }

  private clearOfflineSessionExpiryCheck(): void {
    if (!this.offlineSessionExpiryTimer) return;

    window.clearTimeout(this.offlineSessionExpiryTimer);
    this.offlineSessionExpiryTimer = null;
  }

  private async readOfflineSession(): Promise<OfflineAuthSession | null> {
    const encryptedSession = this.storage.getItem(OFFLINE_AUTH_SESSION_KEY);
    if (!encryptedSession) return null;

    try {
      return await this.decryptOfflineSession(encryptedSession);
    } catch {
      this.storage.removeItem(OFFLINE_AUTH_SESSION_KEY);
      return null;
    }
  }

  private async encryptOfflineSession(session: OfflineAuthSession): Promise<string | null> {
    if (!crypto?.subtle) return null;

    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await this.offlineEncryptionKey();
    const encodedSession = new TextEncoder().encode(JSON.stringify(session));
    const encryptedBytes = new Uint8Array(await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: this.bytesToArrayBuffer(iv) },
      key,
      this.bytesToArrayBuffer(encodedSession),
    ));
    const payload: EncryptedSessionPayload = {
      iv: this.bytesToBase64(iv),
      data: this.bytesToBase64(encryptedBytes),
    };

    return JSON.stringify(payload);
  }

  private async decryptOfflineSession(encryptedSession: string): Promise<OfflineAuthSession | null> {
    if (!crypto?.subtle) return null;

    const payload = JSON.parse(encryptedSession) as EncryptedSessionPayload;
    if (!payload?.iv || !payload?.data) return null;

    const key = await this.offlineEncryptionKey();
    const iv = this.bytesToArrayBuffer(this.base64ToBytes(payload.iv));
    const data = this.bytesToArrayBuffer(this.base64ToBytes(payload.data));
    const decryptedBytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data);
    return JSON.parse(new TextDecoder().decode(decryptedBytes)) as OfflineAuthSession;
  }

  private async offlineEncryptionKey(): Promise<CryptoKey> {
    const material = `${window.location.origin}|tops-offline-auth-v1`;
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(material));
    return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  }

  private microsoftAuthConfig(): Promise<MicrosoftAuthConfig> {
    if (!this.authConfigPromise) {
      this.authConfigPromise = firstValueFrom(this.http.get<MicrosoftAuthConfig>(`${environment.backendApiBaseUrl}/auth/microsoft/config`))
        .catch((error) => {
          this.authConfigPromise = null;
          throw error;
        });
    }
    return this.authConfigPromise;
  }

  private validAccount(account: AuthAccount | null | undefined): account is AuthAccount {
    return !!account?.username;
  }

  private bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach((byte) => binary += String.fromCharCode(byte));
    return btoa(binary);
  }

  private base64ToBytes(value: string): Uint8Array {
    const binary = atob(value);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  }

  private bytesToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  }

  private authLog(message: string, context?: Record<string, unknown>): void {
    console.debug(`[AuthService] ${message}`, context || '');
  }
}